import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorCode, UNKNOWN_ERROR_MESSAGE } from './api-error-code';
import { ProblemDetail, problemCode } from '../models/problem-detail.model';

/** Czech messages a call site wants shown instead of the API's own detail, keyed by code. */
export type ApiErrorMessages = Partial<Record<ApiErrorCode, string>>;

export interface ApiErrorDescription {
  code?: string;
  message: string;
  /**
   * Whether the frontend should report this to Sentry: only a failure the API never answered — a
   * network error, a body-less response, a non-HTTP throw. A coded response is already captured
   * by the backend, so reporting it here would only duplicate it.
   */
  report: boolean;
}

export function apiErrorCode(error: unknown): string | undefined {
  return error instanceof HttpErrorResponse
    ? problemCode(error.error as ProblemDetail | null)
    : undefined;
}

export function isApiError(error: unknown, code: ApiErrorCode): boolean {
  return apiErrorCode(error) === code;
}

/**
 * What to tell the user about a failed request. A call site's own message for the code wins;
 * otherwise the API's detail is shown as it is — it is written for the user — and only a response
 * that explains nothing (or an `INTERNAL_ERROR`, which explains nothing trustworthy) falls back to
 * the generic sentence.
 */
export function describeApiError(
  error: unknown,
  overrides: ApiErrorMessages = {},
): ApiErrorDescription {
  const code = apiErrorCode(error);
  if (code === undefined) {
    return { message: UNKNOWN_ERROR_MESSAGE, report: true };
  }
  const override = overrides[code as ApiErrorCode];
  if (override) {
    return { code, message: override, report: false };
  }
  const detail = ((error as HttpErrorResponse).error as ProblemDetail).detail?.trim();
  if (detail && code !== ApiErrorCode.INTERNAL_ERROR) {
    return { code, message: detail, report: false };
  }
  return { code, message: UNKNOWN_ERROR_MESSAGE, report: false };
}
